import React, { useEffect } from "react";
import { useFormikContext } from "formik";

import LayerCard from "../../elements/LayerCard";
import type {
  L3Context,
  TargetFormValues,
  TeachingProcedure,
} from "../../TargetBuilderPanel.types";

import DiscreteTrialProcedureView from "./DiscreteTrialProcedure";
import NETProcedureView from "./NETProcedure";
import TaskAnalysisProcedureView from "./TaskAnalysisProcedure";
import FreeOperantProcedureView from "./FreeOperantProcedure";
import BehaviorReductionFields from "./BehaviorReductionFields";
import CaregiverFields from "./CaregiverFields";

const DEFAULT_PROMPT_HIERARCHY = {
  levels: [] as string[],
  count_prompted_as_correct: false,
  mastery_prompt_level: "independent",
} as const;

const DEFAULT_COMMON = {
  intervention_strategies: [] as string[],
  intervention_instructions: "",
  generalization_plan: "",
  teaching_opportunities_per_session: "",
  date_of_implementation: "",
  function: "",
  assessment_used: "",
  common_antecedents: "",
  antecedent_strategies: "",
  linked_replacement_behavior: "",
  safety_considerations: "",
  coaching_modeling_notes: "",
  coaching_rehearsal_notes: "",
  coaching_feedback_notes: "",
  coaching_visual_supports_notes: "",
  error_correction_procedure: "",
  practice_opportunities_per_session: "",
} as const;

/** Factory for the initial teaching procedure shape based on L3 context. */
const buildDefaultProcedure = (l3: L3Context): TeachingProcedure => {
  switch (l3) {
    case "discrete-trial":
      return {
        type: "discrete-trial",
        ...DEFAULT_COMMON,
        sd_wording: "",
        correct_response: "",
        error_correction: "",
        first_trial_type: "",
        prompt_hierarchy: { ...DEFAULT_PROMPT_HIERARCHY },
      };
    case "net":
      return {
        type: "net",
        ...DEFAULT_COMMON,
        opportunity_definition: "",
        reinforcement_notes: "",
        prompt_hierarchy: { ...DEFAULT_PROMPT_HIERARCHY },
      };
    case "task-analysis":
      return {
        type: "task-analysis",
        ...DEFAULT_COMMON,
        chaining: "",
        steps: ["", ""],
        step_scoring: "",
        step_advancement_criteria: "",
        prompt_hierarchy: { ...DEFAULT_PROMPT_HIERARCHY },
      };
    case "free-operant":
      return {
        type: "free-operant",
        ...DEFAULT_COMMON,
        operational_definition: "",
        typical_contexts: "",
        antecedent_conditions: "",
        response_consequence_notes: "",
      };
  }
};

interface TeachingProcedureSectionProps {
  /**
   * Goal's behavior subject (client / caregiver) — when "caregiver",
   * the prompt hierarchy is hidden (replaced by coaching config in a future iteration).
   */
  behaviorSubject?: "client" | "caregiver";
  replacementBehaviorTargetOptions?: Array<{ id: number; title: string }>;
}

/**
 * Strategy selector for the Section 2.5 Teaching Procedure block.
 * Picks the right per-L3 form component and seeds default values for the
 * teaching procedure when the user first lands on a new L3 context.
 *
 * Prompt-hierarchy hide rules (per spec):
 *   - l2_method === "anchored-rating-scale" → magnitude doesn't use prompts
 *   - behaviorSubject === "caregiver"        → coaching config replaces prompts
 *   - target_type === "behavior-reduction"   → behavior-reduction targets don't use prompt hierarchy
 */
const TeachingProcedureSection: React.FC<TeachingProcedureSectionProps> = ({
  behaviorSubject = "client",
  replacementBehaviorTargetOptions = [],
}) => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  const { l3_context, l2_method, target_type, teaching_procedure } = values;

  // Seed default procedure when L3 is selected without an existing procedure.
  useEffect(() => {
    if (!l3_context) return;
    if (teaching_procedure?.type !== expectedType(l3_context)) {
      setFieldValue("teaching_procedure", buildDefaultProcedure(l3_context));
    }
  }, [l3_context]);

  if (!l3_context) return null;

  const hideBasePrompt = l2_method === "anchored-rating-scale" || behaviorSubject === "caregiver";

  const hidePromptHierarchy = hideBasePrompt || target_type === "behavior-reduction";
  const isCaregiver = behaviorSubject === "caregiver";
  const makeL3Optional = isCaregiver;

  return (
    <LayerCard
      title="How will this target be taught?"
      description="Configure the specific teaching strategies and procedures for this target"
    >
      {l3_context === "discrete-trial" && (
        <DiscreteTrialProcedureView
          hidePromptHierarchy={hidePromptHierarchy}
          hideFirstTrialType={hideBasePrompt}
          hideTeachingOpportunities={isCaregiver}
          fieldsOptional={makeL3Optional}
        />
      )}
      {l3_context === "net" && (
        <NETProcedureView
          hidePromptHierarchy={hidePromptHierarchy}
          hideReinforcement={isCaregiver}
          hideTeachingOpportunities={isCaregiver}
          fieldsOptional={makeL3Optional}
        />
      )}
      {l3_context === "task-analysis" && (
        <TaskAnalysisProcedureView
          hidePromptHierarchy={hidePromptHierarchy}
          hideTeachingOpportunities={isCaregiver}
          fieldsOptional={makeL3Optional}
        />
      )}
      {l3_context === "free-operant" && (
        <FreeOperantProcedureView fieldsOptional={makeL3Optional} />
      )}

      {target_type === "behavior-reduction" && (
        <BehaviorReductionFields
          replacementBehaviorTargetOptions={replacementBehaviorTargetOptions}
        />
      )}

      {isCaregiver && <CaregiverFields />}
    </LayerCard>
  );
};

const expectedType = (l3: L3Context): TeachingProcedure["type"] => {
  switch (l3) {
    case "discrete-trial":
      return "discrete-trial";
    case "net":
      return "net";
    case "task-analysis":
      return "task-analysis";
    case "free-operant":
      return "free-operant";
  }
};

export default TeachingProcedureSection;
